import { useCallback, useState } from "react";
import type {
  FlashCard,
  FlashCardUI,
  FlashCardData,
} from "../models/FlashcardUI";
import type {
  FlashcardsObject,
  GetFlashCardQuestionsProgressResponse,
  Questions,
} from "../models/FlashcardProgressResponse";
import { resolveInitialFlashcardIndex } from "./flashcardResumeIndex";

type SessionState = {
  flashcards: FlashCardUI[];
  questionIds: string[];
  currentIndex: number;
  questionProgress: Questions[];
};

export const useFlashcardSession = () => {
  const [state, setState] = useState<SessionState>({
    flashcards: [],
    questionIds: [],
    currentIndex: 0,
    questionProgress: [],
  });

  const initialize = useCallback(
    (
      cards: FlashCardData | undefined,
      progressData?: GetFlashCardQuestionsProgressResponse,
    ) => {
      if (cards === undefined) {
        return;
      }

      const rawCards = cards.flashcards ?? [];
      const mapped = mapFlashcardsToUI(
        rawCards,
        progressData?.data?.flashcards,
      );
      const resumeIndex = resolveInitialFlashcardIndex(mapped);
      const maxIdx = Math.max(mapped.length - 1, 0);

      setState((prev) => {
        const prevIds = prev.flashcards.map((c) => c.flashcardId).join(",");
        const nextIds = rawCards.map((c) => c.flashcardId).join(",");
        const sameDeck =
          prev.flashcards.length > 0 &&
          prevIds === nextIds &&
          nextIds.length > 0;

        let nextIndex: number;
        if (mapped.length === 0) {
          nextIndex = 0;
        } else if (!sameDeck) {
          nextIndex = Math.min(Math.max(resumeIndex, 0), maxIdx);
        } else {
          nextIndex = Math.min(Math.max(prev.currentIndex, 0), maxIdx);
        }

        return {
          flashcards: mapped,
          questionIds: cards.questionIds ?? [],
          questionProgress: progressData?.data?.questions ?? [],
          currentIndex: nextIndex,
        };
      });
    },
    [],
  );

  const updateCard = useCallback(
    (id: number, updater: (card: FlashCardUI) => FlashCardUI) => {
      setState((prev) => ({
        ...prev,
        flashcards: prev.flashcards.map((card) =>
          card.flashcardId === id ? updater(card) : card,
        ),
      }));
    },
    [],
  );

  const goNext = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentIndex: Math.min(
        prev.currentIndex + 1,
        Math.max(prev.flashcards.length - 1, 0),
      ),
    }));
  }, []);

  const goPrevious = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentIndex: Math.max(prev.currentIndex - 1, 0),
    }));
  }, []);

  const setIndex = useCallback((index: number) => {
    setState((prev) => {
      const maxIdx = Math.max(prev.flashcards.length - 1, 0);
      const clamped = Math.max(0, Math.min(index, maxIdx));
      if (prev.currentIndex === clamped) return prev;
      return {
        ...prev,
        currentIndex: clamped,
      };
    });
  }, []);

  return {
    state,
    initialize,
    updateCard,
    goNext,
    goPrevious,
    setIndex,
  };
};

const mapFlashcardsToUI = (
  cards: FlashCard[] = [],
  progressFlashcards: FlashcardsObject[] = [],
): FlashCardUI[] => {
  const progressById = new Map<number, FlashcardsObject>(
    progressFlashcards
      .filter((item) => typeof item.flashcardId === "number")
      .map((item) => [item.flashcardId as number, item]),
  );

  return cards.map((card) => ({
    ...card,
    isBookmarkSelected:
      progressById.get(card.flashcardId ?? -1)?.isBookmarked ?? false,
    isExpanded: false,
    isLiked: progressById.get(card.flashcardId ?? -1)?.isLiked ?? false,
    isDisLiked: progressById.get(card.flashcardId ?? -1)?.isDisLiked ?? false,
    likedCount: progressById.get(card.flashcardId ?? -1)?.likedCount ?? 0,
    isCompleted: progressById.get(card.flashcardId ?? -1)?.isCompleted ?? false,
  }));
};
